// app/frontend/pages/Auth/ResetPassword.tsx
import { useState } from "react";
import { useForm } from "@mantine/form";
import { Stack, Anchor } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconAlertCircle } from "@tabler/icons-react";
import { L2KH1, L2KText } from "../../components/ui/Typography";
import { L2KCard } from "../../components/ui/Cards";
import { L2KPasswordInput, L2KButton } from "../../components/ui/Forms";
import AuthFormLayout from "../../components/layouts/AuthFormLayout";
import { SEO } from "../../components/common/SEO";

interface Props {
  reset_password_token: string;
}

export default function ResetPassword({ reset_password_token }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  const form = useForm({
    initialValues: {
      password: "",
      password_confirmation: "",
    },
    validate: {
      password: (value) =>
        value.length < 6 ? "Password must be at least 6 characters" : null,
      password_confirmation: (value, values) =>
        value !== values.password ? "Passwords do not match" : null,
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setIsLoading(true);
    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;

    try {
      const formData = new FormData();
      formData.append("user[password]", values.password);
      formData.append("user[password_confirmation]", values.password_confirmation);
      formData.append("user[reset_password_token]", reset_password_token);

      const response = await fetch("/password", {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        setResetComplete(true);
        notifications.show({
          color: "green",
          title: "Password Reset!",
          message: "Your password has been updated successfully.",
          icon: <IconCheck />,
          autoClose: 5000,
        });
      } else {
        const data = await response.json().catch(() => ({}));
        const errors = data.errors || {};

        notifications.show({
          color: "red",
          title: "Reset Failed",
          message:
            Object.values(errors).flat().join(", ") ||
            "Could not reset password. The link may have expired.",
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });

        if (errors.reset_password_token) {
          form.setErrors({
            password: "Reset link has expired. Please request a new one.",
          });
        }
      }
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "An error occurred. Please try again.",
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (resetComplete) {
    return (
      <AuthFormLayout>
        <SEO title="Password Reset Complete - Learntokiz" />
        <Stack gap="lg">
          <Stack gap="xs" ta="center">
            <L2KH1 variant="accent">Password Updated!</L2KH1>
            <L2KText variant="secondary">
              Your password has been reset successfully.
            </L2KText>
          </Stack>

          <L2KCard variant="elevated">
            <Stack gap="md" ta="center">
              <L2KText variant="secondary">
                You can now sign in with your new password.
              </L2KText>
              <L2KButton
                fullWidth
                size="md"
                onClick={() => (window.location.href = "/login")}
              >
                Sign In
              </L2KButton>
            </Stack>
          </L2KCard>
        </Stack>
      </AuthFormLayout>
    );
  }

  return (
    <AuthFormLayout>
      <SEO title="Reset Password - Learntokiz" />
      <Stack gap="lg">
        <Stack gap="xs" ta="center">
          <L2KH1 variant="accent">Reset Password</L2KH1>
          <L2KText variant="secondary">
            Enter your new password below.
          </L2KText>
        </Stack>

        <L2KCard variant="elevated" glow>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <L2KPasswordInput
                label="New Password"
                placeholder="At least 6 characters"
                required
                {...form.getInputProps("password")}
              />
              <L2KPasswordInput
                label="Confirm New Password"
                placeholder="Confirm your new password"
                required
                {...form.getInputProps("password_confirmation")}
              />
              <L2KButton
                fullWidth
                mt="md"
                size="md"
                type="submit"
                loading={isLoading}
              >
                Reset Password
              </L2KButton>
            </Stack>
          </form>
        </L2KCard>

        <L2KText size="sm" ta="center">
          <Anchor href="/login" style={{ color: "#FF9800" }}>
            Back to Sign In
          </Anchor>
        </L2KText>
      </Stack>
    </AuthFormLayout>
  );
}
